import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(localStorage.getItem('token') || null)
  const user = ref(null)
  const isLoadingUser = ref(false) // 添加加载标志，防止重复调用
  
  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  
  // Actions
  async function login(username, password) {
    try {
      const response = await api.post('/api/auth/login', {
        username,
        password
      })
      
      token.value = response.data.access_token
      user.value = response.data.user
      
      // 保存token到localStorage
      localStorage.setItem('token', token.value)
      
      return { success: true }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.detail || '登录失败'
      }
    }
  }
  
  async function logout() {
    try {
      await api.post('/api/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // 清除本地状态
      token.value = null
      user.value = null
      isLoadingUser.value = false
      localStorage.removeItem('token')
    }
  }
  
  async function fetchUser() {
    if (!token.value) {
      throw new Error('No token available')
    }
    
    // 如果已经有用户信息或正在加载，直接返回
    if (user.value) {
      return user.value
    }
    
    if (isLoadingUser.value) {
      // 等待当前加载完成
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!isLoadingUser.value) {
            clearInterval(checkInterval)
            if (user.value) {
              resolve(user.value)
            } else {
              reject(new Error('Failed to load user'))
            }
          }
        }, 50)
      })
    }
    
    isLoadingUser.value = true
    
    try {
      const response = await api.get('/api/auth/me')
      user.value = response.data
      return user.value
    } catch (error) {
      // Token可能已过期或无效，清除登录状态
      if (error.response?.status === 401 || error.response?.status === 403) {
        token.value = null
        user.value = null
        localStorage.removeItem('token')
      }
      throw error
    } finally {
      isLoadingUser.value = false
    }
  }
  
  function setToken(newToken) {
    token.value = newToken
    localStorage.setItem('token', newToken)
  }
  
  return {
    token,
    user,
    isAuthenticated,
    isAdmin,
    isLoadingUser,
    login,
    logout,
    fetchUser,
    setToken
  }
})
